// src/contexts/FeatureFlagContext.tsx

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { useHospDocData } from "./HospitalDoctorContext";
import {
  getFeatureFlagsByHospitalId,
  patchFeatureFlagsByHospitalId,
} from "../apis/flagfeature";
import { FeatureFlags } from "../utils/types";
import { getCookie, setCookie } from "../utils/cookies";
import { useUserData } from "./UserContext";
import { getWhatsNew } from "../apis/whatsnew";

export interface FeatureFlagContextInterface extends FeatureFlags {
  isLoading: boolean;
  error: Error | null;
  updateFlags: (patch: Partial<FeatureFlags>) => Promise<void>;
}

const defaultFlags: FeatureFlags = {
  raghvendra_payment_flow: false,
  dummy_parent_hospitals: false,
  specialization_flow: false,
  video_consultation: false,
  payments: false,
  no_extend_session: false,
  no_walkin_button: false,
  language_change_buttons: [],
  admin_panel: false,
  analytics: false,
  prescription: false,
  notification: false,
  multiroom: false,
  hide_booking_after_xmin: false,
  multiple_ongoing: false,
  check_in_pause: false,
  multiple_queues: false,
  prn_collection: false,
  wa_lead_collection_via_dropoffs: false,
};

const FeatureFlagContext = createContext<FeatureFlagContextInterface>({
  ...defaultFlags,
  isLoading: false,
  error: null,
  updateFlags: async () => {},
});

export const FeatureFlagProvider = ({ children }: { children: ReactNode }) => {
  const { hospitalID } = useHospDocData();
  const accessToken = getCookie("accessToken");
  const { userData } = useUserData();

  // local state mirrors each flag
  const [flags, setFlags] = useState<FeatureFlags>(defaultFlags);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  // Fetch
  const loadFlags = async (id: string) => {
    setIsLoading(true);
    setError(null);
    try {
      if (accessToken && userData && id && id !== "Home") {
        const res = await getFeatureFlagsByHospitalId(id);
        if (res?.status === 200) {
          console.log("res.data.result[0]", res.data.result[0]);
          setFlags(res.data.result[0]);
        } else {
          setFlags(defaultFlags);
        }
      } else {
        setFlags(defaultFlags);
      }
    } catch (err: any) {
      console.error("Error fetching feature flags:", err);
      setError(err);
      setFlags(defaultFlags);
    } finally {
      setIsLoading(false);
    }
  };

  // Update
  const updateFlags = async (patch: Partial<FeatureFlags>) => {
    if (!hospitalID) throw new Error("No hospital selected");
    setIsLoading(true);
    setError(null);
    try {
      const updated = await patchFeatureFlagsByHospitalId(hospitalID, patch);
      setFlags((prev) => ({ ...prev, ...updated }));
    } catch (err: any) {
      console.error("Error updating feature flags:", err);
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const isWithinDays = (iso?: string, days: number = 7): boolean => {
    if (!iso) return false;
    const created = new Date(iso).getTime();
    if (Number.isNaN(created)) return false;
    const now = Date.now();
    const diffMs = now - created;
    const windowMs = days * 24 * 60 * 60 * 1000;
    return diffMs >= 0 && diffMs <= windowMs;
  };

  const loadWhatsNew = async () => {
    try {
      const res = await getWhatsNew({ from: 1, to: 1 });
      const payload: any = res?.data;
      const apiStatus = payload?.status;
      let isOk = true;
      if (typeof apiStatus === "number") {
        isOk = apiStatus === 1;
      } else if (typeof apiStatus === "string") {
        isOk = apiStatus.toUpperCase() === "SUCCESS";
      }
      let items: any = [];
      if (Array.isArray(payload?.data)) items = payload.data;
      else if (Array.isArray(payload?.result)) items = payload.result;
      else if (Array.isArray(payload?.data?.data)) items = payload.data.data;
      else if (Array.isArray(payload?.result?.features))
        items = payload.result.features;
      else if (Array.isArray(payload?.data?.features))
        items = payload.data.features;
      else if (payload?.data && typeof payload.data === "object")
        items = [payload.data];

      if (getCookie("newFeature") === undefined) {
        if (isWithinDays(items[0].created_at)) {
          setCookie("newFeature", "true", 7);
        } else {
          setCookie("newFeature", "false", 7);
        }
      }
    } catch (e) {
      console.error(e);
    }
  };
  // reload on hospital change
  useEffect(() => {
    if (userData && accessToken && hospitalID && hospitalID !== "Home")
      loadFlags(hospitalID);
    else setFlags(defaultFlags);
    if (userData && accessToken && hospitalID && hospitalID !== "Home")
      loadWhatsNew();
  }, [userData, hospitalID]);

  return (
    <FeatureFlagContext.Provider
      value={{
        // spread all 11 flags:
        ...flags,
        // plus helpers:
        isLoading,
        error,
        updateFlags,
      }}
    >
      {children}
    </FeatureFlagContext.Provider>
  );
};

export const useFeatureFlags = () => useContext(FeatureFlagContext);
